import {
	getVariantPrice,
	moneyToNumber,
} from "@babybjorn/utils/commercetools/money";
import {
	buildCompletePaymentEvent,
	tiktokPixelId,
} from "@babybjorn/utils/tiktok/events";
import type {
	TiktokContentItem,
	TiktokPixelParameters,
	TiktokTrackingData,
} from "@babybjorn/utils/tiktok/types";
import { useEffect } from "react";
import TiktokPixel from "tiktok-pixel";
import { useAppState } from "../../../context/app/context";
import { useCartService } from "../../../hooks/use-cart-service";
import { useCustomEventListener } from "../../browser-events";
import { useHasCookieConsent } from "../../cookies/cookie-consent-service";
import { getCookie } from "../../cookies/cookie-service";
import { getQueryStringParam } from "../../query-string";

export const useTikTok = () => {
	const { siteConfig, userCountry } = useAppState();
	const { cart, setCartCustomField, cartLoading } = useCartService();

	const hasCookieConsent = useHasCookieConsent("targeting");

	useEffect(() => {
		if (!cartLoading && hasCookieConsent) {
			console.log("Initializing TikTok Pixel");
			// Initialize TikTok Pixel
			void TiktokPixel.init(tiktokPixelId, {}).then(() => {
				console.log("TikTok Pixel initialized");
				const currentCartValues: TiktokTrackingData = cart?.custom.fields
					.tiktokTrackingData
					? (JSON.parse(
							cart.custom.fields.tiktokTrackingData,
						) as TiktokTrackingData)
					: {};

				// Tiktok Click ID, only sent when ad is clicked
				const ttclid =
					getQueryStringParam("ttclid") ||
					getCookie("ttclid") ||
					currentCartValues.ttclid;

				// Tiktok Pixel ID generated by Tiktok Pixel Script
				const ttp = getCookie("_ttp") || currentCartValues.ttp;

				if (!ttclid && !ttp) {
					return;
				}

				const tiktokTrackingData: TiktokTrackingData = {
					ttclid: ttclid,
					ttp: ttp,
				};

				const tiktokTrackingDataString = JSON.stringify(tiktokTrackingData);

				if (
					cart?.custom.fields.tiktokTrackingData !== tiktokTrackingDataString
				) {
					void setCartCustomField(
						"tiktokTrackingData",
						tiktokTrackingDataString,
					);
				}
			});
		}
	}, [
		cart?.custom.fields.tiktokTrackingData,
		cartLoading,
		hasCookieConsent,
		setCartCustomField,
	]);

	useCustomEventListener("ViewVariant", ({ detail }) => {
		const variantPrice = getVariantPrice(
			detail.variant,
			siteConfig.currency,
			userCountry,
		);

		const params: TiktokPixelParameters = {
			content_type: "product",
			content_name: detail.product.name,
			description: detail.product.description,
			content_id: detail.variant.sku || undefined,
			currency: variantPrice.currencyCode,
			value: moneyToNumber(variantPrice),
		};

		TiktokPixel.track("ViewContent", params);
	});

	useCustomEventListener("AddToCart", ({ detail }) => {
		const params: TiktokPixelParameters = {
			content_type: "product",
			contents: detail.items.map((item) => {
				return {
					content_id: item.variant.sku || undefined,
					content_name: item.product.name,
					price: moneyToNumber(
						getVariantPrice(
							item.variant,
							detail.cart.totalPrice.currencyCode,
							detail.cart.country,
						),
					),
				};
			}),
			value: moneyToNumber(detail.cart.totalPrice),
			currency: detail.cart.totalPrice.currencyCode,
		};

		TiktokPixel.track("AddToCart", params);
	});

	useCustomEventListener("InitiateCheckout", ({ detail }) => {
		const contents = detail.cart.lineItems.map(
			(lineItem): TiktokContentItem => ({
				content_id: lineItem.variant.sku,
				content_name: lineItem.name[detail.cart.locale ?? "en-US"],
				price: moneyToNumber(lineItem.price.value),
				quantity: lineItem.quantity,
			}),
		);

		const params: TiktokPixelParameters = {
			content_type: "product",
			contents,
			currency: detail.cart.totalPrice.currencyCode,
			value: moneyToNumber(detail.cart.totalPrice),
		};

		TiktokPixel.track("InitiateCheckout", params);
	});

	useCustomEventListener("OrderCreated", ({ detail }) => {
		const params = buildCompletePaymentEvent(detail.order);

		TiktokPixel.track("CompletePayment", params);
	});
};
